import React from 'react';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useShiftRequestForm } from '@/hooks/attendance/myAttendance/useShiftRequestForm';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';


interface ShiftRequestFormProps {
  onSuccess?: () => void;
}

export const ShiftRequestForm: React.FC<ShiftRequestFormProps> = ({ onSuccess }) => {
  const {
    dateFrom,
    dateTo,
    requesterComment,
    errors,
    isSubmitting,
    setValue,
    handleFormSubmit,
    minDateFrom,
    minDateTo,
    maxDateTo,
    translate,
  } = useShiftRequestForm({ onSuccess });

  return (
    <SidePanelFormLayout
      onSubmit={handleFormSubmit}
      data-testid="shift-request-form"
    >
      <SidePanelFormLayout.Fields>
        {/* Date From */}
        <DatePicker
          label={`${translate('attendance:shiftChange.form.dateFrom')}`}
          value={dateFrom}
          onChange={(newValue) => {
            if (newValue) {
              setValue('dateFrom', newValue);
            }
          }}
          color={errors.dateFrom ? 'error' : undefined}
          supportingText={errors.dateFrom?.message ? translate(errors.dateFrom.message) : undefined}
          minDate={minDateFrom}
          required
        />

        {/* Date To */}
        <DatePicker
          label={`${translate('attendance:shiftChange.form.dateTo')}`}
          value={dateTo}
          onChange={(newValue) => {
            if (newValue) {
              setValue('dateTo', newValue);
            }
          }}
          color={errors.dateTo ? 'error' : undefined}
          supportingText={errors.dateTo?.message ? translate(errors.dateTo.message) : undefined}
          minDate={minDateTo}
          maxDate={maxDateTo}
          required
        />

        {/* Comments */}
        <TextArea
          label={translate('attendance:shiftChange.form.comments')}
          value={requesterComment || ''}
          onChange={(value) => setValue('requesterComment', value)}
          disabled={isSubmitting}
          color={errors.requesterComment ? 'error' : undefined}
          supportingText={
            errors.requesterComment?.message
              ? translate(errors.requesterComment.message)
              : undefined
          }
          maxLength={500}
          showCharacterCount
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="create">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {translate('attendance:shiftChange.form.submit')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default ShiftRequestForm;
